import {
  Component,
  ElementRef,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  ComponentRef,
} from '@angular/core';
import { FavoriteHtsNumberModel } from '@app/data-layer/models/common/favorite-hts-number.model';
import { enumBinaCatalogItemType } from '@enum/enumBinaCatalogItemType';
import { UtilsHelper } from '@helpers/utils-helper';
import { BINA_SECOND_GROUP_NUM, BINA_THIRD_GROUP_NUM } from '@shared/bina-mutual/components/const';
import { BinaCatalogItemTitleEditSidbarComponent } from '../bina-catalog-item-title-edit-sidebar/bina-catalog-item-title-edit-sidebar.component';
import { AppService } from '@app/app.service';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import {
  BINA_CATALOG_SUPPORTED_TYPE_ELEMENT_LIST,
  BinaCatalogSupprtedTypeElementsModel,
} from '@constants/bina-catalog-supported-type-elements';
import { enumBinaCatalogItemElementType } from '@enum/enumBinaCatalogItemElementType';
import { enumHtsNumberTariffStatus } from '@app/data-layer/enums/common/enumHtsNumberTariffStatus';

@Component({
  selector: 'app-bina-catalog-item',
  templateUrl: './bina-catalog-item.component.html',
  styleUrls: ['./bina-catalog-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BinaCatalogItemComponent implements OnInit, AfterViewInit {
  @Input() public isShowHoverBackground: boolean = false;
  @Input() public isWithMinHeight: boolean = true;
  @Input() public maxItemDescriptionLines: number = 3;
  @Input() public truncationCharLimit: number = 205;
  @Input() public type: enumBinaCatalogItemType = enumBinaCatalogItemType.UPDATED_AND_NOT_SUBJECT_TO_QUOTAS;
  @Input() public favoriteHtsNumber: FavoriteHtsNumberModel;
  @Output() public delete: EventEmitter<number> = new EventEmitter<number>();
  @Output() public htsInsert: EventEmitter<FavoriteHtsNumberModel> = new EventEmitter<FavoriteHtsNumberModel>();
  @Output() public openCalculator: EventEmitter<FavoriteHtsNumberModel> = new EventEmitter<FavoriteHtsNumberModel>();
  @Output() public reclassify: EventEmitter<{ existingDescription: string; itemIdToDelete: number }> = new EventEmitter<{
    existingDescription: string;
    itemIdToDelete: number;
  }>();

  @ViewChild('binaCatalogItemDescription', { static: true })
  public binaCatalogItemDescriptionRef: ElementRef;

  public enumBinaCatalogItemElementType: typeof enumBinaCatalogItemElementType = enumBinaCatalogItemElementType;
  public enumHtsNumberTariffStatus: typeof enumHtsNumberTariffStatus = enumHtsNumberTariffStatus;
  public BINA_SECOND_GROUP_NUM: number = BINA_SECOND_GROUP_NUM;
  public BINA_THIRD_GROUP_NUM: number = BINA_THIRD_GROUP_NUM;
  public currentItemDescriptionLines: number;
  public isItemDescriptionTruncated: boolean;
  public isShowingMore: boolean;
  public isDutiesRatesToggled: boolean;
  public htsExternalSearchUrl: string = 'https://hts.usitc.gov/search?query=';

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private appService: AppService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {}

  ngAfterViewInit() {
    const container = this.binaCatalogItemDescriptionRef.nativeElement;
    const lineHeight = parseInt(getComputedStyle(container).lineHeight, 10);
    this.currentItemDescriptionLines = Math.floor(container.scrollHeight / lineHeight);
    this.isItemDescriptionTruncated = this.currentItemDescriptionLines > this.maxItemDescriptionLines;
    this.cdr.detectChanges();
  }

  public getCodeGroupAt(startingIndex: number, length: number = 2): string {
    const codeToReturn = this.favoriteHtsNumber?.htsNumber?.substring(startingIndex, startingIndex + length);
    return codeToReturn.length === length ? codeToReturn : null;
  }

  public isHasSupportForItemElement(elementType: enumBinaCatalogItemElementType): boolean {
    const supportedTypeElementsItem: BinaCatalogSupprtedTypeElementsModel = BINA_CATALOG_SUPPORTED_TYPE_ELEMENT_LIST.find(
      (item) => item.type === this.type,
    );
    return supportedTypeElementsItem.elements.some((element) => element === elementType);
  }

  public onShowMore() {
    this.isShowingMore = !this.isShowingMore;
    this.cdr.markForCheck();
  }

  public onDutiesToggle() {
    this.isDutiesRatesToggled = !this.isDutiesRatesToggled;
  }

  public onDeleteItem(id: number) {
    this.delete.emit(id);
  }

  public onCalculatorClick() {
    this.openCalculator.emit(this.favoriteHtsNumber);
  }

  public onReclassifyClick() {
    this.reclassify.emit({
      existingDescription: this.favoriteHtsNumber.searchQuery,
      itemIdToDelete: this.favoriteHtsNumber.id,
    });
  }

  public openCatalogItemTitleEditSidebar(favoriteHtsId: number, favoriteHtsTitle: string): void {
    this.appService.isXlg$.next(true);

    const componentSidebarRef: ComponentRef<BinaCatalogItemTitleEditSidbarComponent> = this.appService.openSidebarAsWindow(
      BinaCatalogItemTitleEditSidbarComponent,
      {
        sidebarHeaderText: '',
        isMd: true,
      },
    );

    componentSidebarRef.instance.favoriteHtsId = favoriteHtsId;
    componentSidebarRef.instance.favoriteHtsTitle = favoriteHtsTitle;

    componentSidebarRef.instance.save.pipe(takeUntil(this.destroy$)).subscribe((updatedTitle) => {
      this.favoriteHtsNumber.title = updatedTitle;
      this.cdr.markForCheck();
    });

    componentSidebarRef.instance.close.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    });
  }

  public get truncatedHtsDescription() {
    return UtilsHelper.limitLength(this.favoriteHtsNumber.description, this.truncationCharLimit);
  }

  public get unitOfQuantity(): string {
    return this.favoriteHtsNumber.quantityUnits.join(', ');
  }

  public get specialProgramIndicators(): string {
    return this.favoriteHtsNumber.specialProgramIndicators.join(', ');
  }

  public get isTariffStatusChanged(): boolean {
    return this.favoriteHtsNumber.tariffStatus !== enumHtsNumberTariffStatus.NOT_CHANGED;
  }

  public get isTariffChanged(): boolean {
    return this.favoriteHtsNumber.tariffStatus === enumHtsNumberTariffStatus.TARIFF_CHANGED;
  }

  public get isTariffIncreased(): boolean {
    return this.favoriteHtsNumber.tariffStatus === enumHtsNumberTariffStatus.TARIFF_INCREASED;
  }

  public get isTariffDecreased(): boolean {
    return this.favoriteHtsNumber.tariffStatus === enumHtsNumberTariffStatus.TARIFF_DECREASED;
  }

  public get tariffStatusIndicatorIconSrc(): string {
    if (!this.isTariffStatusChanged) {
      return '';
    }

    if (this.isTariffIncreased) {
      return 'assets/icons/icon_caret_up_sm_red.svg';
    }

    if (this.isTariffDecreased) {
      return 'assets/icons/icon_caret_down_sm_green.svg';
    }
  }
}
